import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MerchantService } from 'src/app/services/merchant/merchant.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import Swal from 'sweetalert2';

/**
 * USUARIOS DEL PORTAL DE UN COMERCIO — pantalla propia.
 *
 * 🔴 Por qué esta pantalla existe: hasta que se construyó, la única forma de
 * crear el usuario de un comercio era un guion en el servidor. Un comercio dado
 * de alta desde la consola quedaba SIN PODER ENTRAR a su portal y no había
 * ninguna señal de eso — por eso la cinta avisa cuando no hay ninguno activo,
 * que es el estado que antes era invisible.
 *
 * ── POR QUÉ LAS DOS LISTAS VAN UNA DEBAJO DE LA OTRA, Y NO EN SUB-PESTAÑAS ──
 *
 * Eran dos sub-solapas DENTRO de la solapa «Usuarios» de la ficha: para llegar
 * a una lista de nueve filas había que elegir tres veces (la ficha, la solapa,
 * la sub-solapa). La decisión de Juan del 2026-09-19 —«si tengo muchísimas
 * cajas voy a tener que bajar, y si tengo muchos usuarios voy a tener que
 * bajar; sepáramelo en la vista principal»— era contra el APILADO DENTRO DE LA
 * FICHA, donde arriba de las listas había todavía un encabezado, una fila de
 * pestañas y una tarjeta.
 *
 * Con ruta propia ese techo desapareció: la página empieza en las listas. Y las
 * dos están acotadas —15 filas por página cada una, con su conteo en el
 * rótulo—, así que la página no puede crecer sin fin: el piso es siempre
 * visible. Las PERSONAS van primero porque son pocas y son las que más se
 * consultan; las CAJAS, que son las que crecen (una por puesto de cobro de cada
 * tienda), van abajo y no empujan a nadie.
 *
 * 📍 La pregunta que esta pantalla contesta es «¿quién entra al portal de este
 * comercio?», y esa pregunta NO se contesta con media respuesta. Escondiendo
 * una de las dos listas detrás de un control, el estado «no tiene ninguna caja»
 * sólo se ve si a alguien se le ocurre ir a buscarlo.
 */
@Component({
  selector: 'app-comercios-usuarios',
  templateUrl: './comercios-usuarios.component.html'
})
export class ComerciosUsuariosComponent implements OnInit, OnDestroy {

  codigo = '';
  comercio: any = null;
  /** Las tiendas del comercio: hacen falta para nombrar la tienda de cada caja. */
  tiendas: any[] = [];
  cargando = true;
  noEncontrado = false;

  usuarios: any[] = [];
  usuariosCargados = false;
  cargandoUsuarios = false;

  readonly porPagina = 15;

  // ── los dos grupos: PERSONAS y CAJAS ──────────────────────────────────────
  //
  // 🔴 Una caja y una persona entran las dos con un usuario, pero no son lo
  // mismo. La CAJA es un puesto de cobro: vive en una tienda, tiene número y no
  // tiene correo ni dueño — el que atiende esa tarde es el que la usa. La
  // PERSONA es alguien del comercio que entra a mirar sus ventas.
  //
  // Mezcladas en una sola tabla la diferencia no se veía: la columna «Rol»
  // decía COMERCIO en todas las filas, que es lo mismo que no decir nada.

  personas: any[] = [];
  personasFiltradas: any[] = [];
  personasPaginadas: any[] = [];
  busquedaPersona = '';
  paginaPersona = 1;

  cajas: any[] = [];
  cajasFiltradas: any[] = [];
  cajasPaginadas: any[] = [];
  busquedaCaja = '';
  /** El código de la tienda por la que se filtra. Vacío = todas. */
  filtroTiendaCaja = '';
  paginaCaja = 1;

  private subQuery?: Subscription;
  private timerBusqueda: any = null;

  constructor(
    private ruta: ActivatedRoute,
    private router: Router,
    private _merchant: MerchantService,
    public _auth: AuthService
  ) { }

  // RBAC (2026-10-06): los usuarios del portal tienen su propio permiso,
  // config.comercios_usuarios.* (create para dar de alta; update para reponer la
  // clave y activar o desactivar). La API exige lo mismo.
  puede = (accion: string): boolean => this._auth.hasPermission(`config.comercios_usuarios.${accion}`);

  ngOnInit(): void {
    this.ruta.paramMap.subscribe(p => {
      this.codigo = p.get('code') || '';
      this.cargar();
    });

    // Los filtros y las dos páginas viven en la URL: el enlace se comparte, el
    // botón «atrás» deshace, y recargar no pierde dónde se estaba.
    this.subQuery = this.ruta.queryParams.subscribe((qp: Params) => {
      this.busquedaPersona = qp['persona'] || '';
      this.busquedaCaja = qp['caja'] || '';
      this.filtroTiendaCaja = qp['tienda'] || '';
      this.paginaPersona = Number(qp['pag_persona']) > 0 ? Number(qp['pag_persona']) : 1;
      this.paginaCaja = Number(qp['pag_caja']) > 0 ? Number(qp['pag_caja']) : 1;
      this.aplicarFiltros();
    });
  }

  ngOnDestroy(): void {
    this.subQuery?.unsubscribe();
    clearTimeout(this.timerBusqueda);
  }

  /**
   * 🔴 SIN trackBy, Angular destruye y vuelve a crear cada fila en cada ciclo de
   * detección de cambios. Y cuando eso pasa, los botones de la fila DEJAN DE
   * RESPONDER: el `mousedown` cae en un nodo y el `mouseup` en otro, así que el
   * clic nunca se completa. Ya pasó en este repositorio con los pasos del
   * asistente de planes, y no da error: simplemente no hace nada.
   */
  porId = (_i: number, u: any): string => u?._id;

  /** El comercio y sus tiendas, en una sola llamada. Cero endpoints nuevos. */
  cargar = async (): Promise<void> => {
    this.cargando = true;
    this.noEncontrado = false;
    try {
      const rep = await this._merchant.getMerchant(this.codigo);
      this.comercio = rep?.merchant || null;
      this.tiendas = rep?.shops || [];
      this.noEncontrado = !this.comercio;
    } catch {
      this.comercio = null;
      this.tiendas = [];
      this.noEncontrado = true;
    } finally {
      this.cargando = false;
    }
    if (!this.noEncontrado) await this.cargarUsuarios();
  };

  cargarUsuarios = async (): Promise<void> => {
    this.cargandoUsuarios = true;
    try {
      const r = await this._merchant.getMerchantUsers(this.codigo);
      this.usuarios = r?.usuarios || [];
      this.usuariosCargados = true;
      this.agruparUsuarios();
    } catch (e: any) {
      Swal.fire({ icon: 'error', title: 'No se pudieron cargar los usuarios',
                  text: e?.error?.msg || 'Intenta de nuevo.' });
    } finally { this.cargandoUsuarios = false; }
  };

  get sinUsuariosActivos(): boolean {
    return this.usuariosCargados && !this.usuarios.some(u => u.status);
  }

  /** 🔴 Un solo lugar decide qué es un proveedor: ausente, null u otro valor = comercio normal. */
  get esProveedor(): boolean { return this.comercio?.business_type === 'PROVEEDOR_SERVICIO'; }
  get hayUsuarios(): boolean { return this.usuarios.length > 0; }

  // ══ QUIÉN ES QUIÉN ══════════════════════════════════════════════════════════

  /**
   * ¿Es una caja? El tipo lo manda el servidor.
   *
   * ⚠️ El respaldo por `cash_register` es a propósito: contra un servidor que
   * todavía no manda `tipo`, la pantalla tiene que seguir mostrando la lista
   * entera —todos en Personas— y no quedarse en blanco. Un campo nuevo que
   * falta no puede vaciar una pantalla que antes funcionaba.
   */
  private esCaja = (u: any): boolean =>
    u?.tipo ? u.tipo === 'CAJA'
            : (u?.cash_register !== undefined && u?.cash_register !== null && u?.cash_register !== '');

  /**
   * ¿El servidor sabe distinguir cajas de personas?
   *
   * 🔴 Mientras no mande `tipo` en NINGÚN usuario, un cero en el grupo de cajas
   * no significa «no tiene cajas»: significa «no se puede saber». Y una
   * pantalla que afirma «este comercio no tiene cajas» cuando en realidad no
   * puede verlas miente con toda la cara de estar bien.
   */
  get tipoDeUsuarioDisponible(): boolean {
    return this.usuarios.some(u => !!u?.tipo);
  }

  /** El nombre de la tienda. Sale de las que ya trajo la ficha: no se vuelve a pedir. */
  /**
   * El número de la caja. 🔴 El servidor manda `cash_register` POBLADO
   * (`kredit-api-MT/services/merchant_usuario.js:267`, `populate('cash_register', …)`): es
   * un objeto, no el número. Leído directo pintaba «[object Object]» en la columna, y el
   * orden y la búsqueda comparaban el objeto. Si algún día llega el número suelto, también sirve.
   */
  numeroCaja = (u: any): string => {
    const c = u?.cash_register;
    if (c === null || c === undefined || c === '') return '—';
    if (typeof c === 'object') return c.cash_register_number != null ? String(c.cash_register_number) : '—';
    return String(c);
  };

  nombreTienda = (code: string): string => {
    if (!code) return '—';
    const t = this.tiendas.find(x => x.code === code);
    return t?.name || t?.short_name || code;
  };

  /**
   * EL ROL, EN PALABRAS.
   *
   * 🔴 En pantalla NUNCA va el código de RBAC. «COMERCIO» no le dice nada a
   * nadie, y encima decía lo mismo en todas las filas. Un código que se filtra
   * a la pantalla obliga a quien mira a aprenderse el diccionario del sistema.
   *
   * 📍 Los usuarios que ya existían tienen el rol viejo `COMERCIO`: quedan como
   * administradores, que es lo que son — entran a ver todo el comercio.
   */
  // 🔴 2026-09-24: el mismo diccionario que el portal (`portal-comercios/lib/usuarios.ts`,
  // `rolEnPalabras`). Acá faltaban gerente, ventas y técnico: esos usuarios salían con «—» y
  // la pestaña parecía decir que todos eran administradores. `COMERCIO` (el rol viejo) sigue
  // siendo administrador: entra a ver todo el comercio.
  private readonly ROLES_EN_PALABRAS: any = {
    COMERCIO:         'Administrador',
    COMERCIO_ADMIN:   'Administrador',
    COMERCIO_GERENTE: 'Gerente',
    COMERCIO_VENTAS:  'Ventas',
    COMERCIO_TECNICO: 'Técnico',
    COMERCIO_CAJA:    'Caja',
    COMERCIO_CONTABILIDAD: 'Contabilidad'
  };

  /**
   * 🔴 2026-09-25 (Juan: «en el user no se ven los roles, sólo administración»). UNA palabra: el
   * papel que rige, que manda el núcleo en `rol` con la MISMA prioridad que usa el portal para
   * decidir qué pantallas se ven (`infra/nucleo/papel.js`). Quien tiene el rol viejo `COMERCIO` y
   * además `COMERCIO_GERENTE` entra al portal como gerente: aquí salía «Administrador · Gerente».
   * Si el núcleo todavía no manda `rol`, se toma el primero de la misma prioridad. Con dos papeles
   * gana el más amplio: Contabilidad (sólo mira) va al final y cede ante cualquier otro.
   *
   * ⚠️ Un código que no está en el diccionario NO se muestra crudo: se calla (un guion).
   */
  private readonly PRIORIDAD = ['COMERCIO_CAJA', 'COMERCIO_VENTAS', 'COMERCIO_TECNICO', 'COMERCIO_GERENTE', 'COMERCIO_ADMIN', 'COMERCIO', 'COMERCIO_CONTABILIDAD'];
  rolEnPalabras = (u: any): string | null => {
    const codigos = (u?.roles || []).map((r: string) => String(r || '').toUpperCase());
    const rige = u?.rol || this.PRIORIDAD.find(c => codigos.includes(c));
    return (rige && this.ROLES_EN_PALABRAS[String(rige).toUpperCase()]) || null;
  };

  // ══ LOS DOS GRUPOS, SUS FILTROS Y SUS PÁGINAS ═══════════════════════════════

  agruparUsuarios = (): void => {
    this.cajas = this.usuarios.filter(u => this.esCaja(u)).sort((a, b) =>
      this.nombreTienda(a.shop).localeCompare(this.nombreTienda(b.shop)) ||
      (Number(this.numeroCaja(a)) || 0) - (Number(this.numeroCaja(b)) || 0));
    this.personas = this.usuarios.filter(u => !this.esCaja(u));
    this.aplicarFiltros();
  };

  /** Se llama con cada cambio de la URL y después de cada carga. */
  private aplicarFiltros = (): void => {
    this.filtrarPersonas();
    this.filtrarCajas();
  };

  private filtrarPersonas = (): void => {
    const q = this.busquedaPersona.trim().toLowerCase();
    this.personasFiltradas = q
      ? this.personas.filter(u =>
          `${u.username || ''} ${u.name || ''} ${u.lastName || ''} ${u.email || ''}`
            .toLowerCase().includes(q))
      : [...this.personas];
    const desde = (this.paginaPersona - 1) * this.porPagina;
    this.personasPaginadas = this.personasFiltradas.slice(desde, desde + this.porPagina);
  };

  private filtrarCajas = (): void => {
    const q = this.busquedaCaja.trim().toLowerCase();
    // 📍 El filtro por tienda primero: es el que se usa de verdad. «¿Qué cajas
    // tiene Catia?» es la pregunta que se hace, no «¿dónde está mt.caja7?».
    let out = this.filtroTiendaCaja
      ? this.cajas.filter(c => c.shop === this.filtroTiendaCaja)
      : [...this.cajas];
    if (q) out = out.filter(c =>
      `${c.username || ''} ${c.shop || ''} ${this.nombreTienda(c.shop)} ${this.numeroCaja(c)}`
        .toLowerCase().includes(q));
    this.cajasFiltradas = out;
    const desde = (this.paginaCaja - 1) * this.porPagina;
    this.cajasPaginadas = this.cajasFiltradas.slice(desde, desde + this.porPagina);
  };

  get totalPaginasPersona(): number {
    return Math.max(1, Math.ceil(this.personasFiltradas.length / this.porPagina));
  }

  get totalPaginasCaja(): number {
    return Math.max(1, Math.ceil(this.cajasFiltradas.length / this.porPagina));
  }

  /**
   * Escribe el estado en la URL. Lo vacío no viaja.
   *
   * 📍 `replaceUrl` al teclear: una entrada de historial por letra convierte el
   * botón «atrás» en una máquina de borrar caracteres. Los selectores y las
   * páginas sí dejan historial, porque ahí sí se navegó.
   */
  private actualizarUrl = (opciones: { reemplazar?: boolean } = {}): void => {
    const qp: any = {};
    if (this.busquedaPersona.trim()) qp.persona = this.busquedaPersona.trim();
    if (this.busquedaCaja.trim()) qp.caja = this.busquedaCaja.trim();
    if (this.filtroTiendaCaja) qp.tienda = this.filtroTiendaCaja;
    if (this.paginaPersona > 1) qp.pag_persona = this.paginaPersona;
    if (this.paginaCaja > 1) qp.pag_caja = this.paginaCaja;
    this.router.navigate([], {
      relativeTo: this.ruta, queryParams: qp, replaceUrl: !!opciones.reemplazar
    });
  };

  /** Teclear espera 250 ms: sin esto se escribe la URL en cada letra. */
  alEscribir = (grupo: 'persona' | 'caja'): void => {
    clearTimeout(this.timerBusqueda);
    this.timerBusqueda = setTimeout(() => {
      if (grupo === 'persona') this.paginaPersona = 1; else this.paginaCaja = 1;
      this.actualizarUrl({ reemplazar: true });
    }, 250);
  };

  alFiltrarPorTienda = (): void => { this.paginaCaja = 1; this.actualizarUrl(); };

  irAPaginaPersona = (p: number): void => {
    if (p < 1 || p > this.totalPaginasPersona) return;
    this.paginaPersona = p;
    this.actualizarUrl();
  };

  irAPaginaCaja = (p: number): void => {
    if (p < 1 || p > this.totalPaginasCaja) return;
    this.paginaCaja = p;
    this.actualizarUrl();
  };

  limpiarBusquedaPersona = (): void => {
    this.busquedaPersona = '';
    this.paginaPersona = 1;
    this.actualizarUrl();
  };

  limpiarFiltrosCaja = (): void => {
    this.busquedaCaja = '';
    this.filtroTiendaCaja = '';
    this.paginaCaja = 1;
    this.actualizarUrl();
  };

  get hayFiltroCaja(): boolean { return !!(this.busquedaCaja || this.filtroTiendaCaja); }

  /**
   * Las tiendas del desplegable: TODAS las del comercio, con «Todas» por
   * omisión.
   *
   * 📍 Se listan todas a propósito, incluso las que hoy no tienen ninguna caja:
   * elegir una y ver el bloque vacío ES la respuesta a «¿esta tienda ya tiene
   * cajas?». Escondiéndolas, esa tienda simplemente no aparece y no se sabe si
   * es que no tiene cajas o si el desplegable no la trajo.
   */
  get tiendasParaFiltrar(): any[] { return this.tiendas; }

  // ══ COPIAR AL PORTAPAPELES ══════════════════════════════════════════════════
  //
  // 📍 La dirección propia del portal del comercio (`…/c/MT`) NO se repite acá:
  // ya está en la ficha del comercio, con su botón de copiar. Un mismo dato en
  // dos pantallas son dos lugares donde puede quedar desactualizado.

  /**
   * ⚠️ `navigator.clipboard` NO existe fuera de un contexto seguro: si la
   * consola se abre por IP en http —que es como la abren en las tiendas— es
   * `undefined` y el botón no haría absolutamente nada, sin error. De ahí el
   * respaldo con el textarea.
   */
  private alPortapapeles = async (texto: string): Promise<boolean> => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(texto);
        return true;
      }
    } catch { /* sigue por el respaldo */ }
    try {
      const ta = document.createElement('textarea');
      ta.value = texto;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch { return false; }
  };

  // ══ ACCIONES SOBRE UN USUARIO ═══════════════════════════════════════════════

  /**
   * EL ALTA — no es un diálogo: es su propia pantalla, con su dirección.
   *
   * 🔴 «Entiende que no me gustan los modales» (Juan, 2026-09-19). Y acá había
   * un motivo además del gusto: el camino de la caja pide tienda, caja, usuario,
   * clave y confirmación, y eso apretado en un diálogo tapaba la lista que se
   * vino a mirar.
   */
  irAAltaUsuario = (): void => {
    this.router.navigate(['/console/merchants', this.codigo, 'usuarios', 'nuevo']);
  };

  /**
   * El cartel de acceso, al reponer la clave.
   *
   * 🔴 Es la ÚNICA vez que esta clave se ve. No queda guardada en ningún lado
   * legible: si se pierde, se repone, no se recupera. Por eso el botón de
   * copiar está acá adentro — quien está del otro lado del teléfono la necesita
   * entera, usuario y clave, y dictarla letra por letra es donde se pierde.
   */
  private mostrarAcceso = (usuario: string, clave: string): Promise<any> => {
    const u = usuario || '—';
    const c = clave || 'admin123';
    return Swal.fire({
      icon: 'success',
      title: 'Ya puede entrar',
      html: `<p style="font-size:1.25rem;margin:.75rem 0;line-height:1.7">
               <strong>${u}</strong><br><strong>${c}</strong>
             </p>
             <button type="button" id="kbCopiarAcceso" class="swal2-styled swal2-cancel">
               Copiar usuario y clave
             </button>
             <p style="font-size:.85rem;margin-top:1rem">
               Esta clave <strong>no se vuelve a mostrar</strong>. Si se pierde, se repone con la
               llave de la fila.<br>El sistema le va a exigir cambiarla al entrar.
             </p>`,
      confirmButtonText: 'Listo, ya la anoté',
      didOpen: () => {
        const boton = document.getElementById('kbCopiarAcceso');
        if (!boton) return;
        boton.addEventListener('click', async () => {
          const ok = await this.alPortapapeles(`${u} / ${c}`);
          boton.textContent = ok ? 'Copiado' : 'No se pudo copiar';
        });
      }
    });
  };

  /**
   * ⚠️ Se DESACTIVA, nunca se borra: un usuario que entró dejó rastro, y
   * borrarlo deja esos rastros apuntando a alguien que no existe. Desactivar
   * además le corta la sesión abierta.
   */
  alternarUsuario = async (u: any): Promise<void> => {
    const activar = !u.status;
    if (!activar) {
      const esUnaCaja = this.esCaja(u);
      const c = await Swal.fire({
        icon: 'warning',
        title: esUnaCaja ? `¿Desactivar la caja ${u.cash_register} de ${this.nombreTienda(u.shop)}?`
                         : `¿Desactivar a ${u.username}?`,
        text: esUnaCaja
          ? 'Ese puesto deja de poder cobrar y se le cierra la sesión. Se puede volver a activar.'
          : 'Se le cierra la sesión y no va a poder entrar al portal. Se puede volver a activar.',
        showCancelButton: true, confirmButtonText: 'Desactivar', cancelButtonText: 'No'
      });
      if (!c.isConfirmed) return;
    }
    try {
      await this._merchant.toggleMerchantUser(this.codigo, u._id, activar);
      await this.cargarUsuarios();
    } catch (e: any) {
      Swal.fire({ icon: 'error', title: 'No se pudo cambiar el estado del usuario',
                  text: e?.error?.msg || 'Intenta de nuevo.' });
    }
  };

  reponerClave = async (u: any): Promise<void> => {
    const c = await Swal.fire({
      icon: 'question', title: `¿Reponer la clave de ${u.username}?`,
      text: 'Vuelve a la clave inicial, se le cierra la sesión y el sistema le va a exigir cambiarla.',
      showCancelButton: true, confirmButtonText: 'Reponer la clave', cancelButtonText: 'No'
    });
    if (!c.isConfirmed) return;
    try {
      const r = await this._merchant.resetMerchantUserPassword(this.codigo, u._id);
      await this.mostrarAcceso(u.username, r?.clave_inicial);
    } catch (e: any) {
      Swal.fire({ icon: 'error', title: 'No se pudo reponer la clave',
                  text: e?.error?.msg || 'Intenta de nuevo.' });
    }
  };
}
